import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addInboxDocument,
  confirmDocument,
  disregardInboxDocument,
  getInbox,
  getReview,
  retryRun,
} from '../api/intake-client';
import { INTAKE_MUTATION_META, INTAKE_POLL_MS, INTAKE_QUERY_META, intakeQueryRetry } from './use-intake-run-status';
import type { InboxAddResult, InboxDisregardReason, InboxDisregardResult, InboxDocument, IntakeDocType } from '../types';

/** `IntakeDropZone` and `useUploadIntakeBatch` invalidate exactly this key on upload. Do not rename it. */
export const intakeInboxKey = (patientId: string) => ['intake-inbox', patientId] as const;

const hasInFlight = (docs: InboxDocument[] | undefined) =>
  !!docs?.some((d) => d.status === 'Queued' || d.status === 'Running');

/** The poll rule, on the query's state: every 3 s while a file reads; never after a failed FIRST read. */
export function inboxRefetchInterval(state: { status: string; data: InboxDocument[] | undefined }): number | false {
  if (state.status === 'error' && state.data === undefined) return false;
  return hasInFlight(state.data) ? INTAKE_POLL_MS : false;
}

/**
 * The patient's open inbox (contract §3). Polls every 3 s while any document is Queued or Running, and stops when none
 * is. A failed FIRST read stops the poll too: `intake_disabled` or a lost connection would otherwise be asked for
 * forever. A failed refresh with a list already on screen keeps polling while a file is still reading, so one dropped
 * request never freezes the table.
 */
export function useIntakeInbox(patientId: string | null | undefined, enabled = true) {
  return useQuery<InboxDocument[]>({
    queryKey: intakeInboxKey(patientId ?? ''),
    queryFn: async ({ signal }) => (await getInbox(patientId as string, 'open', signal)).documents,
    enabled: enabled && !!patientId,
    refetchInterval: (query) => inboxRefetchInterval(query.state),
    refetchIntervalInBackground: false,
    retry: intakeQueryRetry,
    meta: INTAKE_QUERY_META,
  });
}

/** The whole inbox history (`status=all`, each document with its `inboxStatus`): read once, never polled. */
export const intakeInboxHistoryKey = (patientId: string) => ['intake-inbox-all', patientId] as const;

/**
 * Where an earlier upload went once it left the open inbox: Added (filed) or Disregarded. Asked only when needed
 * (a duplicate upload whose earlier file is not in the open inbox), so the inbox page never pays for it.
 */
export function useIntakeInboxHistory(patientId: string | null | undefined, enabled: boolean) {
  return useQuery<InboxDocument[]>({
    queryKey: intakeInboxHistoryKey(patientId ?? ''),
    queryFn: async ({ signal }) => (await getInbox(patientId as string, 'all', signal)).documents,
    enabled: enabled && !!patientId,
    retry: intakeQueryRetry,
    meta: INTAKE_QUERY_META,
  });
}

/**
 * Every inbox action refreshes the inbox and its history (an added or disregarded file changes where a later duplicate
 * points). `filed` also refreshes the Filed documents list.
 */
function useRefresh(patientId: string) {
  const queryClient = useQueryClient();
  return (filed: boolean) => {
    void queryClient.invalidateQueries({ queryKey: intakeInboxKey(patientId) });
    void queryClient.invalidateQueries({ queryKey: intakeInboxHistoryKey(patientId) });
    if (filed) void queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
  };
}

/** Adds a non-lab document or a photo. `docType` is sent only when the user changed it. */
export function useAddInboxDocument(patientId: string) {
  const refresh = useRefresh(patientId);
  return useMutation<InboxAddResult, Error, { documentId: string; docType?: IntakeDocType }>({
    mutationFn: ({ documentId, docType }) => addInboxDocument(patientId, documentId, docType),
    meta: INTAKE_MUTATION_META,
    onSuccess: () => refresh(true),
  });
}

export function useDisregardInboxDocument(patientId: string) {
  const refresh = useRefresh(patientId);
  return useMutation<InboxDisregardResult, Error, { documentId: string; reason: InboxDisregardReason; note: string | null }>({
    mutationFn: ({ documentId, reason, note }) => disregardInboxDocument(patientId, documentId, reason, note),
    meta: INTAKE_MUTATION_META,
    onSuccess: () => refresh(false),
  });
}

/** "This is <first name>": reads the run's ETag, then sends `/Document` with `patientConfirmed: true` (contract §7). */
export function useConfirmInboxPatient(patientId: string) {
  const refresh = useRefresh(patientId);
  return useMutation<unknown, Error, { runId: string }>({
    mutationFn: async ({ runId }) => {
      const review = await getReview(patientId, runId);
      return confirmDocument(patientId, runId, { patientConfirmed: true }, review.etag);
    },
    meta: INTAKE_MUTATION_META,
    onSuccess: () => refresh(false),
  });
}

export function useRetryInboxRun(patientId: string) {
  const refresh = useRefresh(patientId);
  return useMutation<unknown, Error, { runId: string }>({
    mutationFn: ({ runId }) => retryRun(patientId, runId),
    meta: INTAKE_MUTATION_META,
    onSuccess: () => refresh(false),
  });
}
